import { ArrowRight } from '@/app/icons/ArrowRight'
import { CheckLineIcon } from '@/app/icons/CheckLineIcon'
import { ICourse } from '@/types/course'
import { Typography } from '@mui/material'
import useEmblaCarousel from 'embla-carousel-react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import resolveConfig from 'tailwindcss/resolveConfig'
import tailwindConfig from '../../../../../tailwind.config'

type CoursePrerequisitesProps = {
  courseInfo: Pick<ICourse, 'prerequisites'>
} & React.HTMLAttributes<HTMLDivElement>

export function CoursePrerequisites({
  courseInfo,
  ...props
}: CoursePrerequisitesProps) {
  const t = useTranslations('CoursePage')
  const { prerequisites } = courseInfo || {}
  const [slideIndex, setSlideIndex] = useState(0)
  const { theme } = resolveConfig(tailwindConfig)

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    breakpoints: {
      [`(min-width: ${theme?.screens?.['md']})`]: {
        slidesToScroll: 4, // TODO: show slides in view on desktop sizes
      },
    },
  })

  useEffect(() => {
    if (!emblaApi) {
      return
    }

    const onSelect = () => {
      const selectedScrollSnap = emblaApi.selectedScrollSnap()

      setSlideIndex(selectedScrollSnap)
    }

    emblaApi.on('select', onSelect)

    return () => void emblaApi.off('select', onSelect)
  }, [emblaApi])

  if (!Array.isArray(prerequisites) || prerequisites.length === 0) {
    return null
  }

  return (
    <div
      {...props}
      className={`relative flex flex-col gap-8 py-6 lg:overflow-hidden lg:rounded-2xl ${props.className}`}
    >
      <div className="flex flex-col gap-8">
        <Typography
          variant="caption"
          className="text-center font-title text-xl font-medium uppercase"
        >
          {t('prerequisites')}
        </Typography>
        <p className="flex items-center justify-center gap-1 text-sm md:hidden">
          <button
            type="button"
            onClick={() => emblaApi?.scrollPrev()}
            className="rounded-full border p-2"
          >
            <ArrowRight className="h-4 w-auto -rotate-180" />
          </button>
          {slideIndex + 1} / {prerequisites.length}
          <button
            type="button"
            onClick={() => emblaApi?.scrollNext()}
            className="rounded-full border p-2"
          >
            <ArrowRight className="h-4 w-auto" />
          </button>
        </p>
      </div>
      <div
        ref={emblaRef}
        className="relative lg:mx-auto lg:w-max lg:max-w-[100%] lg:overflow-hidden lg:p-0"
        data-embla-ref
      >
        <ul className="no-scrollbar flex cursor-grab auto-cols-[minmax(calc(25%),238px)] justify-start lg:grid lg:grid-flow-col lg:px-0">
          {prerequisites.map((prerequisite, index) => (
            <li
              key={index}
              className="mr-8 flex h-auto min-h-56 shrink-0 basis-[calc(88%-2rem)] snap-start flex-col items-start gap-3 overflow-hidden rounded-xl bg-mydra-medium-beige bg-opacity-50 p-6 md:basis-[calc(30%-2rem)] lg:mx-4 lg:basis-[calc(25%-2rem)]"
            >
              <CheckLineIcon className="h-8 w-8 rounded-full bg-mydra-light-background p-2" />
              <p className="ont-sans text-lg text-mydra-black">
                {prerequisite}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
